import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

interface AIQueryAssistantProps {
  databaseId: string;
  onSQLGenerated: (sql: string, explanation: string) => void;
  autoExecute?: boolean;
}

const EXAMPLE_PROMPTS = [
  "Show all users created in the last 7 days",
  "Find the top 10 products by revenue",
  "Count orders grouped by status",
  "Get average order value per customer",
];

export function AIQueryAssistant({ databaseId, onSQLGenerated, autoExecute = false }: AIQueryAssistantProps) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe what you want to query');
      return;
    }

    setLoading(true);
    try {
      const result = await api.generateSQL(databaseId, prompt);
      onSQLGenerated(result.sql, result.explanation);

      if (autoExecute) {
        toast.success('Query generated and executing...', {
          description: result.explanation,
          duration: 3000,
        });
      } else {
        toast.success('Query generated', {
          description: result.explanation,
          duration: 5000,
        });
      }

      setPrompt('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate SQL');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div className="border rounded-lg bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-medium text-sm">AI Query Assistant</h3>
        </div>
        <span className="text-xs text-muted-foreground">Powered by Groq</span>
      </div>

      {/* Input Area */}
      <div className="p-4">
        <div className="relative">
          <Textarea
            placeholder="Ask AI to generate a SQL query... (e.g., Show all users created in the last 7 days)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            className="min-h-[100px] resize-none pr-12 text-sm"
          />
          <Button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            size="icon"
            className="absolute bottom-3 right-3 h-8 w-8"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Examples - only shown when empty */}
      {prompt.length === 0 && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-xs text-muted-foreground">Quick examples:</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_PROMPTS.map((example) => (
              <button
                key={example}
                onClick={() => setPrompt(example)}
                className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted transition-colors text-left"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
